import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Profile } from "@/hooks/profile/types";

export const useUsersQuery = (gymId: string | null, currentUser: Profile | null) => {
  return useQuery({
    queryKey: ["users", gymId, currentUser?.role],
    queryFn: async () => {
      if (!currentUser) return [];

      console.log("Fetching users for role:", currentUser.role, "gymId:", gymId);

      let gymIds: string[] = [];
      let profileUsers: Profile[] = [];

      if (currentUser.role === "admin") {
        console.log("Admin fetching all users");
        
        const { data, error } = await supabase.rpc('get_all_users_with_memberships');
        
        if (error) {
          console.error("Error fetching users for admin:", error);
          throw error;
        }
        
        profileUsers = (data || []) as Profile[];
        
        // Para admin, obtener todos los gym_ids únicos
        gymIds = [...new Set(profileUsers.map(u => u.gym_id).filter(Boolean))] as string[];
      } 
      else if (currentUser.role === "manager") {
        console.log("Manager fetching users for their gyms");
        
        const { data: managerGyms, error: managerError } = await supabase
          .from("gym_managers")
          .select("gym_id")
          .eq("manager_id", currentUser.id);

        if (managerError) {
          console.error("Error fetching manager gyms:", managerError);
          throw managerError;
        }

        if (!managerGyms || managerGyms.length === 0) {
          console.log("Manager has no gyms assigned");
          return [];
        }

        gymIds = managerGyms.map(g => g.gym_id).filter(Boolean) as string[];

        const { data, error } = await supabase.rpc('get_users_by_gym_ids', {
          gym_ids: gymIds
        });
        
        if (error) {
          console.error("Error fetching users for manager:", error);
          throw error;
        }

        profileUsers = (data || []) as Profile[];
      } 
      else if (gymId) {
        console.log("Fetching users for specific gym:", gymId);
        gymIds = [gymId];
        
        const { data, error } = await supabase.rpc('get_users_by_gym_ids', {
          gym_ids: [gymId]
        });
        
        if (error) {
          console.error("Error fetching users for specific gym:", error);
          throw error;
        }
        
        profileUsers = (data || []) as Profile[];
      }

      // Obtener pending_members para los gimnasios relevantes (paginado para evitar límite 1000 de PostgREST)
      if (gymIds.length > 0) {
        const PAGE_SIZE = 1000;
        let pendingMembers: any[] = [];
        let pendingError: any = null;
        let fromIdx = 0;

        while (true) {
          const { data: chunk, error: chunkError } = await supabase
            .from("pending_members")
            .select("*")
            .in("gym_id", gymIds)
            .range(fromIdx, fromIdx + PAGE_SIZE - 1);

          if (chunkError) {
            pendingError = chunkError;
            break;
          }
          if (!chunk || chunk.length === 0) break;
          pendingMembers = pendingMembers.concat(chunk);
          if (chunk.length < PAGE_SIZE) break;
          fromIdx += PAGE_SIZE;
        }

        console.log("Pending members fetched (paginated):", pendingMembers.length);

        if (pendingError) {
          console.error("Error fetching pending members:", pendingError);
        } else if (pendingMembers.length > 0) {
          // Convertir pending_members a formato Profile para mostrar en la lista
          const pendingAsProfiles: Profile[] = pendingMembers.map(pm => ({
            id: pm.id,
            username: pm.email ?? null,
            avatar_url: null,
            role: pm.role,
            first_name: pm.first_name,
            last_name: pm.last_name,
            banned: false,
            gym_id: pm.gym_id,
            created_at: pm.created_at,
            updated_at: pm.created_at,
            birthdate: pm.birthdate ?? null,
            dni: pm.dni,
            phone: pm.phone ?? null,
            address: pm.address ?? null,
            emergency_phone: pm.emergency_phone ?? null,
            health_insurance: pm.health_insurance ?? null,
            isPending: true,
          }));

          profileUsers = [...profileUsers, ...pendingAsProfiles];
          console.log("Added pending members:", pendingMembers.length);
        }
      }

      console.log("Total users fetched:", profileUsers.length);
      return profileUsers;
    },
    enabled: !!currentUser,
  });
};
